import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';

const API_BASE = 'https://api.glowytics.com';

export default function YouTubeCampaignsLab() {
  const [dateRange, setDateRange] = useState('last30');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const brandId =
    typeof window !== 'undefined' ? window.localStorage.getItem('currentBrandId') : null;

  const headers = useMemo(
    () => ({
      'Content-Type': 'application/json',
      ...(brandId ? { 'X-Brand-ID': brandId } : {})
    }),
    [brandId]
  );

  const loadData = async () => {
    if (!brandId) {
      setError('No active brand selected (currentBrandId missing).');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setData(null);

      const params = new URLSearchParams();
      params.set('dateRange', dateRange || 'last30');

      const res = await fetch(
        `${API_BASE}/api/google-ads/youtube-campaigns-live?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'include',
          headers
        }
      );

      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        let msg = `Request failed (${res.status})`;
        if (ct.includes('application/json')) {
          const js = await res.json().catch(() => ({}));
          msg = js.error || js.details || msg;
        }
        throw new Error(msg);
      }

      const payload = await res.json();
      setData(payload);
    } catch (e) {
      console.error('YouTube campaigns lab error:', e);
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Auto-load on first mount
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const campaigns = useMemo(() => {
    if (!data || !Array.isArray(data.campaigns)) return [];
    return data.campaigns
      .map((c) => {
        const impressions = Number(c.impressions || 0);
        const views = Number(c.videoViews || c.video_views || 0);
        const rate = impressions > 0 ? (100 * views) / impressions : 0;
        const costMicros = Number(c.costMicros || c.cost_micros || 0);
        const cost = costMicros > 0 ? costMicros / 1_000_000 : 0;
        const videoIds = Array.isArray(c.videoIds) ? c.videoIds : [];
        return {
          id: c.campaignId || c.campaign_id,
          name: c.campaignName || c.campaign_name,
          impressions,
          videoViews: views,
          videoViewRate: rate,
          cost,
          costMicros,
          videoIds
        };
      })
      .filter((c) => Number(c.cost || 0) > 0)
      .sort((a, b) => b.impressions - a.impressions);
  }, [data]);

  const summary = useMemo(() => {
    if (!data) return null;
    if (data.summary) {
      // Normalize backend summary fields
      const s = data.summary;
      const totalImpressions = Number(s.totalImpressions ?? 0);
      const totalVideoViews = Number(s.totalVideoViews ?? 0);
      const totalCostMicros = Number(s.totalCostMicros ?? s.total_cost_micros ?? 0);
      const totalCost = totalCostMicros > 0 ? totalCostMicros / 1000000 : 0;
      const avg =
        s.avgViewRate != null
          ? Number(s.avgViewRate)
          : s.avgVideoViewRate != null
          ? Number(s.avgVideoViewRate)
          : 0;
      return {
        totalImpressions,
        totalVideoViews,
        totalCost,
        avgViewRate: isFinite(avg) ? avg : 0
      };
    }
    const totalImpressions = campaigns.reduce(
      (sum, c) => sum + (c.impressions || 0),
      0
    );
    const totalVideoViews = campaigns.reduce(
      (sum, c) => sum + (c.videoViews || 0),
      0
    );
    const totalCost = campaigns.reduce((sum, c) => sum + (c.cost || 0), 0);
    const avg =
      totalImpressions > 0
        ? (totalVideoViews / totalImpressions) * 100
        : 0;
    return {
      totalImpressions,
      totalVideoViews,
      totalCost,
      avgViewRate: isFinite(avg) ? avg : 0
    };
  }, [data, campaigns]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Card className="p-5 rounded-2xl shadow-sm mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-red-50 rounded-xl border border-red-100">
              <img
                src="/youtube.svg"
                alt="YouTube"
                className="w-8 h-8 object-contain"
              />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Labs · Google Ads YouTube Campaigns
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Debug view for YouTube placement performance pulled from Google Ads
                (`google_ads_youtube_placement_raw`).
              </p>
              {!brandId && (
                <p className="text-xs text-red-500 mt-1">
                  No `currentBrandId` found in localStorage. Please select a brand.
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <select
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7">Last 7 days</option>
                <option value="last30">Last 30 days</option>
                <option value="last90">Last 90 days</option>
                <option value="thisMonth">This Month</option>
                <option value="lastMonth">Last Month</option>
                <option value="all">All</option>
              </select>
            </div>
            <button
              type="button"
              onClick={loadData}
              disabled={loading}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                loading
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {loading ? 'Loading…' : 'Fetch YouTube performance'}
            </button>
          </div>
        </div>
        {error && (
          <div className="mt-3 text-sm text-red-600">
            {error}
          </div>
        )}
      </Card>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 rounded-xl">
            <p className="text-xs text-gray-500 mb-1">Impressions</p>
            <p className="text-xl font-semibold text-gray-900">
              {summary.totalImpressions?.toLocaleString() ?? '0'}
            </p>
          </Card>
          <Card className="p-4 rounded-xl">
            <p className="text-xs text-gray-500 mb-1">Video Views</p>
            <p className="text-xl font-semibold text-gray-900">
              {summary.totalVideoViews?.toLocaleString() ?? '0'}
            </p>
          </Card>
          <Card className="p-4 rounded-xl">
            <p className="text-xs text-gray-500 mb-1">Avg View Rate</p>
            <p className="text-xl font-semibold text-gray-900">
              {Number(summary.avgViewRate ?? 0).toFixed(2)}
              %
            </p>
          </Card>
          <Card className="p-4 rounded-xl">
            <p className="text-xs text-gray-500 mb-1">Cost</p>
            <p className="text-xl font-semibold text-gray-900">
              {(summary.totalCost ?? 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </p>
          </Card>
        </div>
      )}

      {/* Campaigns table */}
      <Card className="rounded-xl mb-6">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="font-medium text-gray-800">YouTube Campaigns</h2>
          <span className="text-xs text-gray-500">
            {campaigns.length} campaign(s)
          </span>
        </div>
        <div className="p-4 overflow-x-auto">
          {loading && !data ? (
            <div className="text-sm text-gray-500">Loading…</div>
          ) : campaigns.length === 0 ? (
            <div className="text-sm text-gray-500">
              No campaigns found for the selected range.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">
                    Campaign
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">
                    Impressions
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">
                    Video Views
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">
                    View Rate
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">
                    Cost
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {campaigns.map((c) => {
                  const primaryVideoId = c.videoIds?.[0];
                  const thumbUrl = primaryVideoId
                    ? `https://i.ytimg.com/vi/${primaryVideoId}/hqdefault.jpg`
                    : null;
                  const videoUrl = primaryVideoId
                    ? `https://www.youtube.com/watch?v=${primaryVideoId}`
                    : null;
                  return (
                    <tr key={c.id}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-3">
                          {thumbUrl && (
                            <a
                              href={videoUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="flex-shrink-0"
                            >
                              <img
                                src={thumbUrl}
                                alt={c.name || c.id}
                                className="w-16 h-9 rounded-md object-cover border border-gray-200"
                              />
                            </a>
                          )}
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 truncate max-w-xs">
                              {c.name || c.id || '—'}
                            </div>
                            <div className="text-[11px] text-gray-500">
                              {c.id}
                              {primaryVideoId && (
                                <>
                                  {' · '}
                                  <a
                                    href={videoUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-[11px] text-indigo-600 hover:underline"
                                    title="Open advertised YouTube video"
                                  >
                                    {primaryVideoId}
                                  </a>
                                </>
                              )}
                            </div>
                            {!primaryVideoId && (
                              <div className="text-[11px] text-amber-600 mt-0.5">
                                Video ID not found for this campaign (yet).
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {c.impressions.toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        {c.videoViews.toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        {c.videoViewRate.toFixed(2)}%
                      </td>
                      <td className="px-3 py-2">
                        {Number(c.cost || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* Placements intentionally omitted in this lab – focus on campaign-level stats */}
    </div>
  );
}


